import dynamic from 'next/dynamic';
import {
    useCallback,
    useEffect,
    useState,
    createContext,
    useMemo,
} from 'react';
import { useRouter } from 'next/router';
import { getInvestorRelationshipManagerDetails } from '@services/investors.service';
import {
    menuItems,
    sideBarNavItems,
} from '@components/Investor/NewDashboard/helper';
import Cookies from 'universal-cookie';
import useAuthStore from '@store/useAuthStore';
import { getAuth, isAuthenticated } from '@services/identity.service';
import { showReferral } from '@helpers/utils/investor-utils';
import useKycStore from '@store/useKycStore';
import { getOpenConfigurations } from '@services/configuration.service';
import { configurationEntityTypes } from '@helpers/enums/configuration-enums';
import { isValidResponseEntity } from '@helpers/utils';
import {
    Popup,
    PopupFooter,
    PopupHeader,
} from '@ui/atoms/design-system/pop-up';
import routes from '@helpers/routes';
import useWindowDimensions from '@hooks/useWindowDimensions';

const LoginModalV2 = dynamic(
    () => import('@components/Auth/LoginModal/LoginModalV2'),
    { ssr: false }
);

const AuthContext = createContext();

export const AuthProvider = ({ children }) => {
    const auth = getAuth();
    const entityType = auth?.entityType;
    const router = useRouter();
    const { isLogged, logout, investor: investorRecord } = useAuthStore();
    const { reset: resetKycStore } = useKycStore();
    const [showLoginModal, setShowLoginModal] = useState(false);
    const [showSignUpModal, setShowSignUpModal] = useState(false);
    const [showSignUp, setShowSignUp] = useState(false);
    const [menu, setMenu] = useState([]);
    const [rmDetails, setRMDetails] = useState(null);
    const [sideBarItems, setSideBarItems] = useState([]);
    const [investor, setInvestor] = useState({
        isWalletActive: false,
        investor: null,
    });
    const [bondFeatureFlag, setBondFeatureFlag] = useState(false);
    const [showInterestModal, setShowInterestModal] = useState({
        show: false,
        action: null,
    });

    const handleLogout = useCallback((
        { logoutFromClientSideOnly, shouldRedirect } = {
            logoutFromClientSideOnly: false,
            shouldRedirect: true
        }) => {
        logout(router, { logoutFromClientSideOnly, shouldRedirect });
        resetKycStore();
    }, [isLogged, router]);

    const showRMDetails = useMemo(
        () => entityType !== 'ifa' && rmDetails,
        [rmDetails]
    );
    const { width } = useWindowDimensions();

    // TODO: WALLET AND REFERRAL
    const checkKycStatus = useCallback(
        (authenticated) => {
            if (authenticated && investorRecord) {
                setInvestor((prev) => ({
                    ...prev,
                    investor: investorRecord,
                }));
                return investorRecord?.kycStatus === 'Verified';
            }
            return false;
        },
        [isLogged]
    );

    const fetchBondFeatureFlag = async () => {
        const response = await getOpenConfigurations(
            configurationEntityTypes.BOND_DIRECTORY_FEATURE
        );

        if (isValidResponseEntity(response)) {
            const isActive = response?.entity[0]?.value === 'true';
            setBondFeatureFlag(isActive);
            return isActive;
        }
        setBondFeatureFlag(false);
        return false;
    };

    const fetchUserData = async () => {
        checkKycStatus(isLogged);
        let menuItemsList = [...menuItems];

        const isBondFeatureActive = await fetchBondFeatureFlag();

        if (isBondFeatureActive) {
            menuItemsList.map((each) => {
                if (each.link === routes.bonds.base) {
                    each.show = true;
                } else {
                    each;
                }
            });
        }

        menuItemsList = [...menuItemsList.filter((each) => each.show)];
        if (isLogged && entityType === 'ifa') {
            menuItemsList = [
                ...menuItemsList.filter(
                    (each) => each.link !== '/refer-and-earn'
                ),
            ];
        }
        if (isLogged && width < 1280) {
            menuItemsList = [
                ...menuItemsList.filter(
                    (each) => each.link !== '/bond-analyser'
                ),
            ];
        }

        let sideBarItemsList = [
            ...sideBarNavItems?.filter((each) => each.show),
        ];

        if (!showReferral(auth)) {
            setSideBarItems(sideBarItemsList.filter((each) => each.id !== 3));
        } else {
            setSideBarItems(sideBarItemsList);
        }
        setMenu(menuItemsList);
    };

    const fetchRMDetails = async () => {
        const authenticated = await isAuthenticated(auth);
        if (authenticated) {
            const relationshipManagerResponse =
                await getInvestorRelationshipManagerDetails(auth.id);

            setRMDetails(
                relationshipManagerResponse?.entity
                    ?.crmRelationshipManagersContact
            );
        }
    };

    useEffect(() => {
        if (isLogged !== null && width) {
            handleCloseModal();
            fetchRMDetails();
            fetchUserData();
        }
    }, [isLogged, width]);

    const loginModal = useCallback(() => {
        setShowSignUp(false);
        setShowLoginModal(true);
    }, [isLogged, router]);

    const signUpModal = useCallback(() => {
        setShowSignUp(true);
        setShowSignUpModal(true);
    }, [isLogged, router]);

    const handleCloseModal = useCallback(() => {
        const isBondAnalyserPage = router?.pathname.includes('/bond-analyser');
        if (isBondAnalyserPage) {
            router.push(
                {
                    pathname: router.pathname,
                    query: {},
                },
                undefined,
                { scroll: false }
            );
        }
        setShowLoginModal(false);
        setShowSignUpModal(false);
    }, [isLogged, router]);

    const showDealInterestModal = (navigateTo, modelDetails) => {
        setShowInterestModal((prev) => ({
            show: true,
            action: navigateTo,
            ...modelDetails,
        }));
    };

    const closeDealInterestModal = () => {
        setShowInterestModal((prev) => ({
            show: false,
            action: null,
        }));
    };

    const handleOnClickInterest = useCallback(() => {
        closeDealInterestModal();
        router.push(showInterestModal.action);
    }, [showInterestModal]);

    const renderDealInterestModal = () => {
        return (
            <Popup
                isOpen={showDealInterestModal}
                onClose={closeDealInterestModal}
            >
                <PopupHeader
                    title={showInterestModal.title}
                    description={showInterestModal.description}
                    iconType={'success'}
                    iconSize="medium"
                />
                <PopupFooter
                    primaryButtonText={showInterestModal.primaryButtonText}
                    primaryOnClick={handleOnClickInterest}
                    secondaryButtonText={showInterestModal.secondaryButtonText}
                    secondaryOnClick={closeDealInterestModal}
                    buttonStyle="alternate"
                />
            </Popup>
        );
    };

    const loginOrSignupModal = useCallback(() => {
        if (showLoginModal) {
            return (
                <LoginModalV2
                    handleCloseModal={handleCloseModal}
                    showLoginModal={showLoginModal}
                    showSignUp={showSignUp}
                    setShowSignUp={setShowSignUp}
                    showDealInterestModal={showDealInterestModal}
                />
            );
        }
        if (showSignUpModal) {
            return (
                <LoginModalV2
                    handleCloseModal={handleCloseModal}
                    showSignUpModal={showSignUpModal}
                    showSignUp={showSignUp}
                    setShowSignUp={setShowSignUp}
                    showDealInterestModal={showDealInterestModal}
                />
            );
        }
        if (showInterestModal && showInterestModal.show) {
            return renderDealInterestModal();
        }
        return null;
    }, [
        showLoginModal,
        showSignUpModal,
        isLogged,
        showSignUp,
        showInterestModal,
    ]);

    const values = {
        isLogged,
        loginModal,
        signUpModal,
        loginOrSignupModal,
        logout: handleLogout,
        menuItems: menu,
        sideBarItems: isLogged ? sideBarItems : [],
        investor: investor?.investor,
        setInvestor,
        rmDetails,
        showRMDetails,
        bondFeatureFlag,
        showDealInterestModal,
    };

    return (
        <AuthContext.Provider value={values}>{children}</AuthContext.Provider>
    );
};

export default AuthContext;
